import React from 'react';
import { Button } from 'react-bootstrap';
import './DeductionTableRow.scss';

interface DeductionTableRowProps {
  deduction: any;
  index: number;
  t: (key: string) => string;
  onEdit: () => void;
  onRemove: () => void;
}

export const DeductionTableRow: React.FC<DeductionTableRowProps> = ({
  deduction,
  index,
  t,
  onEdit,
  onRemove,
}) => {
  const getDeductionLabel = (type: string) => {
    switch(type) {
      case 'mileage_report': return t('MILEAGE_REPORT') || 'Mileage Report';
      case 'travel_expenses_pdf': return t('TRAVEL_EXPENSES') || 'Travel Expenses';
      case 'expenses_materials_tools_pdf': return t('MATERIALS_AND_TOOLS') || 'Materials & Tools';
      case 'allowance_outside_sweden': return t('ALLOWANCE_OUTSIDE_SWEDEN') || 'Allowance Outside Sweden';
      case 'allowance_in_sweden': return t('ALLOWANCE_IN_SWEDEN') || 'Allowance In Sweden';
      default: return type;
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB');
  };

  const getDateDisplay = () => {
    if (deduction.tripStartDate && deduction.tripEndDate) {
      return `${formatDate(deduction.tripStartDate)} - ${formatDate(deduction.tripEndDate)}`;
    }
    return formatDate(deduction.travelDate || deduction.dateOfPurchase || new Date().toISOString());
  };

  const getAmount = () => {
    if (deduction.amountExVat) {
      const vat = parseFloat(deduction.vat || 0);
      const amount = parseFloat(deduction.amountExVat);
      return (amount + (amount * vat / 100)).toFixed(2);
    }
    if (deduction.total) return deduction.total;
    if (deduction.allowancePerDay) return deduction.allowancePerDay;
    return '-';
  };

  return (
    <div className="table-row deduction-table-row" key={index}>
      <div className="col-drag d-none d-md-flex">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <circle cx="9" cy="12" r="1" fill="#a4a7ae" />
          <circle cx="9" cy="5" r="1" fill="#a4a7ae" />
          <circle cx="9" cy="19" r="1" fill="#a4a7ae" />
          <circle cx="15" cy="12" r="1" fill="#a4a7ae" />
          <circle cx="15" cy="5" r="1" fill="#a4a7ae" />
          <circle cx="15" cy="19" r="1" fill="#a4a7ae" />
        </svg>
      </div>

      <div className="col-dates-allowance" data-label={t('DATE_S')}>
        <span className="date-value">{getDateDisplay()}</span>
      </div>

      <div className="col-allowance-type" data-label={t('ALLOWANCE_TYPE')}>
        <div className="input-box">
          <span className="input-value">{getDeductionLabel(deduction.deductionType)}</span>
        </div>
      </div>

      <div className="col-attachment" data-label={t('ATTACHMENT')}>
        <div className="attachment-box">
          <span className="file-name">
            {deduction.files && deduction.files.length > 0 ? deduction.files[0].name : t('NO_FILE')}
          </span>
          {deduction.files && deduction.files.length > 0 && (
            <Button variant="link" className="download-btn">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path 
                  d="M17.5 12.5V15.8333C17.5 16.2754 17.3244 16.6993 17.0118 17.0118C16.6993 17.3244 16.2754 17.5 15.8333 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V12.5M5.83333 8.33333L10 12.5M10 12.5L14.1667 8.33333M10 12.5V2.5" 
                  stroke="#252525" 
                  strokeWidth="1.67" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                />
              </svg>
            </Button>
          )}
        </div>
      </div>

      <div className="col-price-allowance" data-label={t('PRICE')}>
        <div className="input-box">
          <span className="input-value">{getAmount()}</span>
          <span className="currency-symbol">SEK</span>
        </div>
      </div>

      <div className="col-actions-allowance" data-label={t('ACTIONS')}>
        <Button variant="link" className="edit-btn" onClick={onEdit}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path 
              d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" 
              stroke="#317AF7" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round" 
            />
          </svg>
        </Button>
        <Button variant="link" className="remove-item-btn" onClick={onRemove}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path 
              d="M3 6H5H21M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" 
              stroke="#EF4444" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round" 
            />
          </svg>
        </Button>
      </div>
    </div>
  );
};
